import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { CATEGORIES, getIcon } from "../lib/icons";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Plus, Edit, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";

const ICON_OPTIONS = ["Bone", "Fish", "Cookie", "Gamepad2", "Scissors", "Sparkles", "Link", "Sofa", "Coffee", "HeartPulse", "Target", "Car", "PawPrint"];
const COLOR_OPTIONS = ["#FF7A45", "#38BDF8", "#FDE047", "#A3E635", "#F472B6", "#A78BFA", "#FCA5A5", "#0F172A", "#94A3B8", "#34D399", "#818CF8", "#F87171"];

const empty = {
  name: "", category: "Dog Food", pet_type: "dog", description: "",
  price: 100, in_stock: true, stock: 0, icon: "Bone", color: "#FF7A45", image_url: "", active: true,
  brand: "", size: "", weight: "", ingredients: "", feeding_instructions: "", age_stage: "", search_keywords: "", slug: "",
};

const fullImageUrl = (url) => (url ? `${process.env.REACT_APP_BACKEND_URL}${url}` : null);

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | product

  const load = () => {
    setLoading(true);
    api
      .get("/admin/products")
      .then((r) => setProducts(r.data || []))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const save = async (data, id) => {
    try {
      if (id) {
        await api.put(`/admin/products/${id}`, data);
        toast.success("Product updated");
      } else {
        await api.post("/admin/products", data);
        toast.success("Product created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success("Deleted");
      setProducts((p) => p.filter((x) => x.id !== id));
    } catch (_) {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="admin-products-page">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
        <div>
          <h1 className="font-display text-3xl font-bold">Products</h1>
          <p className="text-sm text-slate-600">Add, edit, and toggle visibility of items.</p>
        </div>
        <button
          onClick={() => setEditing("new")}
          className="neo-btn px-4 py-2 text-white font-bold flex items-center gap-2"
          style={{ background: "var(--pm-dog)" }}
          data-testid="add-product-btn"
        >
          <Plus size={16} /> New Product
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {loading ? (
          <div className="col-span-full text-center text-slate-500 py-10">Loading…</div>
        ) : (
          products.map((p) => {
            const Icon = getIcon(p.icon);
            const imgSrc = fullImageUrl(p.image_url);
            return (
              <div key={p.id} className="neo-card p-4 bg-white" data-testid={`product-row-${p.id}`}>
                <div
                  className="rounded-xl p-4 flex items-center justify-center overflow-hidden aspect-square"
                  style={{ background: p.color, border: "2px solid #0F172A" }}
                >
                  {imgSrc ? (
                    <img
                      src={imgSrc}
                      alt={p.name}
                      loading="lazy"
                      className="w-full h-full object-contain"
                      data-testid={`admin-product-img-${p.id}`}
                    />
                  ) : (
                    <Icon size={48} className="text-white" />
                  )}
                </div>
                <div className="mt-3">
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{p.category}</div>
                  <div className="font-display text-base font-semibold leading-tight">{p.name}</div>
                </div>
                <div className="mt-2 flex items-center justify-between text-sm">
                  <span className="font-bold">{p.price.toFixed(0)} EGP</span>
                  <span
                    className="text-[10px] font-bold px-1.5 py-0.5 rounded border-2 border-slate-900"
                    style={{ background: p.active ? "#A3E635" : "#FCA5A5" }}
                  >
                    {p.active ? "ACTIVE" : "HIDDEN"}
                  </span>
                </div>
                {typeof p.stock === "number" && (
                  <div
                    className="mt-1 text-xs font-semibold"
                    style={{ color: p.stock === 0 ? "#DC2626" : p.stock <= 10 ? "#B45309" : "#64748B" }}
                    data-testid={`admin-stock-${p.id}`}
                  >
                    Stock: {p.stock}
                  </div>
                )}
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() => setEditing(p)}
                    className="neo-btn flex-1 py-1.5 text-xs bg-white flex items-center justify-center gap-1"
                    data-testid={`edit-product-${p.id}`}
                  >
                    <Edit size={12} /> Edit
                  </button>
                  <button
                    onClick={() => del(p.id)}
                    className="neo-btn px-3 py-1.5 text-xs text-white"
                    style={{ background: "#F87171" }}
                    data-testid={`delete-product-${p.id}`}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {editing && (
        <ProductDialog
          initial={editing === "new" ? empty : editing}
          onClose={() => setEditing(null)}
          onSave={save}
          isNew={editing === "new"}
        />
      )}
    </div>
  );
}

function ProductDialog({ initial, onClose, onSave, isNew }) {
  const [form, setForm] = useState({ ...empty, ...initial, image_url: initial.image_url || "" });
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large (max 5 MB)");
      return;
    }
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) {
      toast.error("Only JPG, PNG, or WEBP images are allowed");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // Let axios/browser set Content-Type automatically so the multipart boundary is included.
      const { data } = await api.post("/admin/products/upload-image", fd);
      if (!data || !data.url) {
        throw new Error("Upload response missing url");
      }
      set("image_url", data.url);
      toast.success("Image uploaded");
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || "Upload failed";
      toast.error(typeof detail === "string" ? detail : "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const submit = (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      price: Number(form.price),
      stock: typeof form.stock === "number" ? form.stock : parseInt(form.stock, 10) || 0,
    };
    onSave(payload, isNew ? null : form.id);
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-auto" data-testid="product-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-bold">
            {isNew ? "New Product" : "Edit Product"}
          </DialogTitle>
          <DialogDescription>Configure name, pricing, category, image, icon fallback, color, stock and visibility.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="product-form">
          <Field label="Product Image" full>
            <div className="flex items-center gap-3">
              <div
                className="w-24 h-24 rounded-lg flex items-center justify-center overflow-hidden shrink-0"
                style={{ background: form.color || "#94A3B8", border: "2px solid #0F172A" }}
                data-testid="product-image-preview"
              >
                {form.image_url ? (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${form.image_url}`}
                    alt="Preview"
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <span className="text-xs text-white/80 text-center px-2">Icon fallback</span>
                )}
              </div>
              <div className="flex-1 flex flex-col gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleFile}
                  className="hidden"
                  data-testid="product-image-file"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="neo-btn px-3 py-2 text-sm bg-white inline-flex items-center justify-center gap-2 disabled:opacity-60"
                  data-testid="product-image-upload-btn"
                >
                  <Upload size={14} />
                  {uploading ? "Uploading…" : form.image_url ? "Change Image" : "Upload Image"}
                </button>
                {form.image_url && (
                  <button
                    type="button"
                    onClick={() => set("image_url", "")}
                    className="neo-btn px-3 py-1.5 text-xs bg-white inline-flex items-center justify-center gap-2"
                    data-testid="product-image-remove-btn"
                  >
                    <X size={12} /> Remove image (use icon)
                  </button>
                )}
                <span className="text-xs text-slate-500">JPG/PNG/WEBP • max 5 MB • auto-resized to 800×800</span>
              </div>
            </div>
          </Field>
          <Field label="Name" full>
            <Input required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="product-name" />
          </Field>
          <Field label="Category">
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="product-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => <SelectItem key={c.key} value={c.key}>{c.key}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Pet Type">
            <Select value={form.pet_type} onValueChange={(v) => set("pet_type", v)}>
              <SelectTrigger data-testid="product-pet-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="dog">Dog</SelectItem>
                <SelectItem value="cat">Cat</SelectItem>
                <SelectItem value="both">Both</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Price (EGP)">
            <Input type="number" min="1" step="1" required value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="product-price" />
          </Field>
          <Field label="Stock Quantity">
            <Input
              type="number"
              min="0"
              step="1"
              value={form.stock ?? 0}
              onChange={(e) => set("stock", e.target.value === "" ? 0 : Math.max(0, parseInt(e.target.value, 10) || 0))}
              data-testid="product-stock"
            />
            <span className="text-xs text-slate-500">0 = Out of Stock (Add to Cart disabled)</span>
          </Field>
          <Field label="Icon">
            <Select value={form.icon} onValueChange={(v) => set("icon", v)}>
              <SelectTrigger data-testid="product-icon"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ICON_OPTIONS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Color">
            <div className="flex flex-wrap gap-2 mt-1" data-testid="product-color-swatches">
              {COLOR_OPTIONS.map((c) => (
                <button
                  type="button"
                  key={c}
                  onClick={() => set("color", c)}
                  className="w-8 h-8 rounded-md"
                  style={{
                    background: c,
                    border: form.color === c ? "3px solid #FDE047" : "2px solid #0F172A",
                  }}
                  aria-label={`Color ${c}`}
                />
              ))}
            </div>
          </Field>
          <Field label="Description" full>
            <Textarea rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="product-description" />
          </Field>
          <Field label="Brand">
            <Input value={form.brand} onChange={(e) => set("brand", e.target.value)} placeholder="e.g. Royal Canin" data-testid="product-brand" />
          </Field>
          <Field label="Age Stage">
            <Input value={form.age_stage} onChange={(e) => set("age_stage", e.target.value)} placeholder="e.g. Puppy, Adult, Senior" data-testid="product-age-stage" />
          </Field>
          <Field label="Size">
            <Input value={form.size} onChange={(e) => set("size", e.target.value)} placeholder="e.g. Small / Medium" data-testid="product-size" />
          </Field>
          <Field label="Weight">
            <Input value={form.weight} onChange={(e) => set("weight", e.target.value)} placeholder="e.g. 2 kg" data-testid="product-weight" />
          </Field>
          <Field label="Ingredients" full>
            <Textarea rows={2} value={form.ingredients} onChange={(e) => set("ingredients", e.target.value)} data-testid="product-ingredients" />
          </Field>
          <Field label="Feeding Instructions" full>
            <Textarea rows={2} value={form.feeding_instructions} onChange={(e) => set("feeding_instructions", e.target.value)} data-testid="product-feeding-instructions" />
          </Field>
          <Field label="Search Keywords" full>
            <Input value={form.search_keywords} onChange={(e) => set("search_keywords", e.target.value)} placeholder="Comma-separated, e.g. puppy, chicken, grain-free" data-testid="product-search-keywords" />
          </Field>
          <Field label="SEO Slug" full>
            <Input value={form.slug} onChange={(e) => set("slug", e.target.value)} placeholder="Leave blank to auto-generate from name" dir="ltr" data-testid="product-slug" />
          </Field>
          <div className="flex items-center gap-3">
            <Label className="text-sm font-semibold">In Stock</Label>
            <Switch checked={form.in_stock} onCheckedChange={(v) => set("in_stock", v)} data-testid="product-in-stock" />
          </div>
          <div className="flex items-center gap-3">
            <Label className="text-sm font-semibold">Active (visible in shop)</Label>
            <Switch checked={form.active} onCheckedChange={(v) => set("active", v)} data-testid="product-active" />
          </div>
          <div className="sm:col-span-2 flex justify-end gap-2 mt-2">
            <button type="button" onClick={onClose} className="neo-btn px-4 py-2 bg-white">Cancel</button>
            <button
              type="submit"
              className="neo-btn px-4 py-2 text-white font-bold"
              style={{ background: "var(--pm-dog)" }}
              data-testid="save-product-btn"
            >
              {isNew ? "Create" : "Save Changes"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, full }) {
  return (
    <label className={`flex flex-col gap-1 ${full ? "sm:col-span-2" : ""}`}>
      <span className="text-sm font-semibold">{label}</span>
      {children}
    </label>
  );
}
